/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

/*
 * Rollback (fail back) of a completed deployment: POST /deployments/{id}/actions {"rollback"}.
 * RollbackCard sits on the deployment's overview and follows rollback_status / rollback_tasks;
 * RollbackDialog asks what to do with the deployed VM and the source VM.
 */

import * as React from "react";
import styled from "styled-components";
import {
  CircleCheck,
  CircleX,
  LoaderCircle,
  Power,
  PowerOff,
  RotateCcw,
  Timer,
  Trash2,
  Undo2,
} from "lucide-react";

import { providerLabel } from "@src/components/modules/EndpointModule/EndpointLogos/ProviderMark";
import Tasks from "@src/components/modules/TransferModule/Tasks";
import { ActionButton, SectionCard } from "@src/components/ui/DetailsKit";
import { T9, parseApiDate } from "@src/components/ui/Lists/ListTable";
import {
  CheckRow,
  FootNote,
  FormSection,
  LinkButton,
  Notice,
  OpsDialog,
  RadioCards,
  SectionHeading,
} from "@src/components/ui/OpsDialog";
import cutoverSource, { apiErrorMessage } from "@src/sources/CutoverSource";
import notificationStore from "@src/stores/NotificationStore";

import type { DeploymentRollbackFields, RollbackTargetAction } from "@src/@types/Cutover";
import type { Instance } from "@src/@types/Instance";
import type { DeploymentItemDetails } from "@src/@types/MainItem";
import type { Task } from "@src/@types/Task";

import { formatDuration, formatWhen, relative, shortInstance } from "./cutoverUtils";

export type RollbackDeployment = DeploymentItemDetails & DeploymentRollbackFields;

export type RollbackView =
  | "none"
  | "available"
  | "running"
  | "rolled_back"
  | "failed"
  | "unsupported"
  | "unknown_support"
  | "disabled"
  | "expired";

/** Which rollback state a deployment is in, for the card and the row menus. */
export const rollbackView = (d?: RollbackDeployment | null): RollbackView => {
  if (!d) return "none";
  if (d.rollback_status === "running") return "running";
  if (d.rollback_status === "rolled_back") return "rolled_back";
  if (d.rollback_status === "failed") return d.rollback_allowed ? "failed" : "failed";
  if (d.last_execution_status !== "COMPLETED") return "none";
  if (d.rollback_allowed) return "available";
  if (d.rollback_supported === false) return "unsupported";
  if (d.rollback_supported === null) return "unknown_support";
  if (d.rollback_retention_hours === 0) return "disabled";
  if (d.rollback_supported === undefined && d.rollback_allowed === undefined) return "none";
  return "expired";
};

const progressOf = (tasks: Task[] = []) => {
  if (!tasks.length) return 0;
  const done = tasks.filter(t => ["COMPLETED", "ERROR", "CANCELED", "FAILED_TO_SCHEDULE"].includes(t.status)).length;
  const running = tasks.filter(t => t.status === "RUNNING").length;
  return Math.round(((done + running * 0.5) / tasks.length) * 100);
};

const lastMessage = (tasks: Task[] = []) => {
  const running = tasks.find(t => t.status === "RUNNING") || tasks.filter(t => t.progress_updates?.length).slice(-1)[0];
  const updates: any[] = running?.progress_updates || [];
  const last = updates[updates.length - 1];
  if (!last) return null;
  return typeof last === "string" ? last : last.message;
};

/** rollback_tasks as the Tasks list takes them (the contract also allows plain message strings). */
export const normaliseTasks = (tasks: any[] = [], fallbackTime?: string | null): Task[] =>
  tasks.map((t, i) => ({
    id: t.id || `rollback-${i}`,
    status: t.status || "PENDING",
    created_at: t.created_at || fallbackTime || "",
    updated_at: t.updated_at || t.created_at || fallbackTime || "",
    task_type: t.task_type,
    instance: t.instance,
    depends_on: t.depends_on || [],
    exception_details: t.exception_details || "",
    progress_updates: (t.progress_updates || []).map((u: any, j: number) =>
      typeof u === "string"
        ? { index: j, message: u, created_at: t.updated_at || fallbackTime || "", total_steps: null, current_step: null }
        : u,
    ),
  }));

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;
const Big = styled.div`
  flex: 1;
  min-width: 240px;
  .t {
    font-size: 14px;
    font-weight: 600;
    color: ${T9.slate900};
  }
  .s {
    font-size: 13px;
    color: ${T9.slate500};
    margin-top: 2px;
  }
`;
const Meter = styled.div`
  height: 6px;
  border-radius: 9999px;
  background: ${T9.slate100};
  overflow: hidden;
  margin-top: 12px;
  > div {
    height: 100%;
    border-radius: 9999px;
    transition: width 400ms ease;
  }
`;
const TasksFrame = styled.div`
  margin: 14px -20px -18px -20px;
  border-top: 1px solid ${T9.slate100};
`;

type CardProps = {
  deployment: RollbackDeployment;
  sourceType?: string | null;
  targetType?: string | null;
  instancesDetails: Instance[];
  onRollbackClick: () => void;
  now?: number;
};

export const RollbackCard = (props: CardProps) => {
  const d = props.deployment;
  const view = rollbackView(d);
  const [showTasks, setShowTasks] = React.useState(view === "running");
  const now = props.now || Date.now();
  const tasks = normaliseTasks(d.rollback_tasks, d.rollback?.requested_at);
  const target = providerLabel(props.targetType) || "the target platform";
  const who = d.rollback?.requested_by?.name;

  if (view === "none") return null;

  const tasksList = tasks.length ? (
    showTasks ? (
      <TasksFrame>
        <Tasks items={tasks} instancesDetails={props.instancesDetails} />
      </TasksFrame>
    ) : null
  ) : null;
  const toggleTasks = tasks.length ? (
    <LinkButton type="button" onClick={() => setShowTasks(!showTasks)}>
      {showTasks ? "Hide the rollback tasks" : `Show the rollback tasks (${tasks.length})`}
    </LinkButton>
  ) : null;

  if (view === "available") {
    const until = parseApiDate(d.rollback_available_until || null);
    const deployedAt = parseApiDate(d.deployed_at || null);
    const total = until && deployedAt ? until.getTime() - deployedAt.getTime() : 0;
    const left = until ? Math.max(0, until.getTime() - now) : 0;
    return (
      <SectionCard
        title="Rollback"
        icon={Undo2}
        subtitle={`Undo this cut-over: SwiftIQ powers off or deletes the deployed VM${d.instances?.length === 1 ? "" : "s"}, then powers the source back on.`}
        actions={
          <ActionButton type="button" variant="secondary" onClick={props.onRollbackClick} data-test-id="rollback-open">
            <RotateCcw size={16} />
            Roll back…
          </ActionButton>
        }
        testId="rollbackCard"
      >
        <Row>
          <Big>
            <div className="t">Available until {formatWhen(d.rollback_available_until, true)}</div>
            <div className="s">
              <Timer size={12} style={{ verticalAlign: -1, marginRight: 4 }} />
              {formatDuration(left)} left
              {d.rollback_retention_hours ? ` of the ${d.rollback_retention_hours} h rollback window` : ""}
              {deployedAt ? ` · deployed ${formatWhen(d.deployed_at)}` : ""}
            </div>
          </Big>
        </Row>
        {total ? (
          <Meter>
            <div style={{ width: `${Math.max(2, (left / total) * 100)}%`, background: left / total < 0.15 ? "var(--t9-accent-500)" : T9.brand500 }} />
          </Meter>
        ) : null}
      </SectionCard>
    );
  }

  if (view === "running") {
    const pct = progressOf(tasks);
    return (
      <SectionCard
        title="Rolling back"
        icon={LoaderCircle}
        subtitle={`Started ${relative(d.rollback?.requested_at) === "now" ? "just now" : relative(d.rollback?.requested_at)}${who ? ` by ${who}` : ""} · target: ${
          d.rollback?.target_action === "delete" ? "delete the deployed VM" : "power off the deployed VM"
        }${d.rollback?.power_on_source ? ", then power the source back on" : ""}`}
        actions={toggleTasks}
        testId="rollbackCard"
      >
        <Row>
          <Big>
            <div className="t">{lastMessage(tasks) || "Waiting for a SwiftIQ worker…"}</div>
            <div className="s">
              {tasks.filter(t => t.status === "COMPLETED").length} of {tasks.length || "?"} tasks done
            </div>
          </Big>
        </Row>
        <Meter>
          <div style={{ width: `${Math.max(4, pct)}%`, background: "var(--t9-accent-500)" }} />
        </Meter>
        {tasksList}
      </SectionCard>
    );
  }

  if (view === "rolled_back") {
    return (
      <SectionCard title="Rolled back" icon={CircleCheck} actions={toggleTasks} testId="rollbackCard">
        <Notice tone="success">
          Rolled back {formatWhen(d.rollback?.finished_at, true)}
          {who ? ` by ${who}` : ""}. The deployed VM{d.instances?.length === 1 ? " was" : "s were"}{" "}
          {d.rollback?.target_action === "delete" ? "deleted with their resources" : "turned off and kept"}
          {d.rollback?.power_on_source ? ", and the source was powered back on." : "; the source was left as it was."} The
          transfer is kept, so it can sync again and be cut over later.
        </Notice>
        {tasksList}
      </SectionCard>
    );
  }

  if (view === "failed") {
    return (
      <SectionCard
        title="Rollback failed"
        icon={CircleX}
        actions={
          <>
            {toggleTasks}
            {d.rollback_allowed ? (
              <ActionButton type="button" variant="secondary" onClick={props.onRollbackClick}>
                <RotateCcw size={16} />
                Try again…
              </ActionButton>
            ) : null}
          </>
        }
        testId="rollbackCard"
      >
        <Notice tone="danger" title="The rollback did not complete">
          {d.rollback?.error || "A rollback task failed; open the tasks for details."}
        </Notice>
        {tasksList}
      </SectionCard>
    );
  }

  const messages: { [v: string]: string } = {
    unsupported: `Rollback isn't available for ${target}: its SwiftIQ provider can't power off or delete the deployed VMs.`,
    unknown_support: `SwiftIQ can't tell right now whether ${target} supports rollback (no SwiftIQ worker answered). Try again in a few minutes.`,
    disabled: "Rollback is turned off for this deployment (its rollback window is 0 hours).",
    expired: `The rollback window has ended${d.rollback_retention_hours ? ` (${d.rollback_retention_hours} h after the deployment completed)` : ""}. The deployment can no longer be rolled back from SwiftIQ.`,
  };
  return (
    <SectionCard title="Rollback" icon={Undo2} testId="rollbackCard">
      <Notice tone="neutral">{messages[view]}</Notice>
    </SectionCard>
  );
};

// ------------------------------------------------------------------ dialog

type DialogProps = {
  deployment: RollbackDeployment;
  sourceType?: string | null;
  targetType?: string | null;
  onClose: () => void;
  onStarted?: (deployment: RollbackDeployment | null) => void;
};

export const RollbackDialog = (props: DialogProps) => {
  const d = props.deployment;
  const sourceCanPowerOn = d.rollback_power_on_source_supported !== false;
  const [powerOnSource, setPowerOnSource] = React.useState(sourceCanPowerOn);
  const [targetAction, setTargetAction] = React.useState<RollbackTargetAction>("power_off");
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const source = providerLabel(props.sourceType) || "the source platform";
  const target = providerLabel(props.targetType) || "the target platform";
  const vms = (d.instances || []).map(shortInstance);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const updated = await cutoverSource.rollback(d.id, { powerOnSource, targetAction });
      notificationStore.alert("Rollback started. SwiftIQ shows its progress on this page.", "success");
      props.onStarted?.((updated as any) || null);
      props.onClose();
    } catch (err: any) {
      setError(apiErrorMessage(err, "The rollback could not be started."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <OpsDialog
      title="Roll back deployment"
      subtitle={`${vms.join(", ")} on ${target}`}
      icon={Undo2}
      tone={targetAction === "delete" ? "danger" : "warning"}
      width={620}
      onRequestClose={props.onClose}
      testId="rollbackDialog"
      footer={
        <>
          <FootNote>
            {d.rollback_available_until ? `Available until ${formatWhen(d.rollback_available_until, true)} (${relative(d.rollback_available_until)}).` : ""}
          </FootNote>
          <ActionButton type="button" variant="secondary" onClick={props.onClose} disabled={saving}>
            Cancel
          </ActionButton>
          <ActionButton
            type="button"
            variant={targetAction === "delete" ? "danger" : "primary"}
            onClick={submit}
            disabled={saving}
            data-test-id="rollback-submit"
          >
            {saving ? <LoaderCircle size={16} className="spin" /> : <RotateCcw size={16} />}
            {saving ? "Starting…" : targetAction === "delete" ? "Delete and roll back" : "Roll back"}
          </ActionButton>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        {error ? (
          <Notice tone="danger" title="SwiftIQ refused the rollback">
            {error}
          </Notice>
        ) : null}
        <div style={{ fontSize: 13, color: T9.slate600 }}>
          SwiftIQ adds a rollback run to this deployment: it deals with the deployed VM{vms.length === 1 ? "" : "s"} first,
          then powers the source back on. The transfer is kept, so you can sync again and cut over later.
        </div>
        <FormSection>
          <SectionHeading>Deployed VM{vms.length === 1 ? "" : "s"} on {target}</SectionHeading>
          <RadioCards
            name="target_action"
            value={targetAction}
            onChange={v => setTargetAction(v as RollbackTargetAction)}
            options={[
              {
                value: "power_off",
                label: "Power off and keep",
                icon: PowerOff,
                description: "The VM stays, powered off, so it can be inspected or started again.",
              },
              {
                value: "delete",
                label: "Delete with its resources",
                icon: Trash2,
                danger: true,
                description: "Deletes the VM and what the deployment created for it: disks, NICs, public IP and security group. This cannot be undone.",
              },
            ]}
          />
          {targetAction === "delete" ? (
            <Notice tone="danger">
              Anything written on the deployed VM{vms.length === 1 ? "" : "s"} since the cut-over is lost.
            </Notice>
          ) : null}
        </FormSection>
        <FormSection>
          <SectionHeading>Source VM{vms.length === 1 ? "" : "s"} on {source}</SectionHeading>
          <CheckRow
            checked={powerOnSource}
            disabled={!sourceCanPowerOn}
            onChange={setPowerOnSource}
            label={
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <Power size={14} />
                Power the source VM{vms.length === 1 ? "" : "s"} back on
              </span>
            }
            description={
              sourceCanPowerOn
                ? "After the deployed VM is stopped or deleted. SwiftIQ checks that every source VM still exists before it starts."
                : `${source} can't power VMs on from SwiftIQ: power ${vms.length === 1 ? "the server" : "the servers"} on by hand after the rollback.`
            }
            testId="rollback-poweron"
          />
        </FormSection>
      </div>
    </OpsDialog>
  );
};
